import { StatusDefinition, allowedStatuses } from './Status'

export interface UserRetrieve {
    id: string
    accountId: string
    status: string
    firstName: string
    lastName: string
}

export interface UserUpdatePayload {
    firstName: string
    lastName: string
    // The status key (see UserStatus.key), not the status value
    status: string
    remarks?: string
}

export interface UserStatus extends StatusDefinition {
    key: number
    needReason: boolean
}

export default class User {
    static journey = ['pending_docs', 'pending_approval', 'approved']

    static hints: Record<string, string> = {
        pending_docs: 'Waiting for the user to upload documents',
        pending_approval: 'Documents submitted, waiting for review',
        approved: 'Verified and able to transact',
        rejected: 'Application declined',
        documents_need_resubmission: 'The user must submit documents again',
        closed: 'Account has been closed',
        suspended: 'Temporarily blocked from transacting',
        dormant: 'Marked as unused',
        terminated: 'Relationship ended by Wallex',
        withdrawn: 'Application withdrawn by the user',
    }

    static statuses(user: UserRetrieve): UserStatus[] {
        const statuses: Array<UserStatus> = [
            {
                key: 1,
                value: 'pending_docs',
                description: 'Pending Documents',
                needReason: false,
                available: [],
            },
            {
                key: 2,
                value: 'pending_approval',
                description: 'Pending Approval',
                needReason: false,
                available: ['pending_docs', 'documents_need_resubmission'],
            },
            {
                key: 3,
                value: 'approved',
                description: 'Approved',
                needReason: false,
                available: ['pending_approval', 'suspended', 'dormant'],
            },
            {
                key: 4,
                value: 'rejected',
                description: 'Rejected',
                needReason: true,
                available: ['pending_approval'],
            },
            {
                key: 5,
                value: 'documents_need_resubmission',
                description: 'Document Need Resubmission',
                needReason: true,
                available: ['pending_approval'],
            },
            {
                key: 7,
                value: 'closed',
                description: 'Closed',
                needReason: false,
                available: ['approved', 'suspended', 'dormant'],
            },
            {
                key: 9,
                value: 'suspended',
                description: 'Suspended',
                needReason: true,
                available: ['approved', 'dormant'],
            },
            {
                key: 10,
                value: 'dormant',
                description: 'Dormant',
                needReason: true,
                available: ['approved'],
            },
            {
                key: 11,
                value: 'terminated',
                description: 'Terminated',
                needReason: true,
                available: ['approved', 'suspended', 'dormant'],
            },
            {
                key: 12,
                value: 'withdrawn',
                description: 'Withdrawn',
                needReason: false,
                available: ['pending_docs', 'documents_need_resubmission'],
            },
        ]

        return allowedStatuses(statuses, user.status)
    }
}
